import React from 'react';
import { Alert, Col, Form, InputGroup, Row } from 'react-bootstrap';
import type { ProjectData } from './types';

// Per-project настройки KYC. Сохраняются в project.project_data.kyc.
// Документация: mock_ss/docs/INTEGRATION_FRONTEND.md §«Per-project настройка».
//
// MVP (Phase 0): поля сохраняются в admin-panel БД, но kyc-svc их пока
// НЕ читает — он использует глобальный env SUMSUB_LEVEL. Когда kyc-команда
// расширит kyc.proto под level_name/project_id (см. OQ-BE-1 в doc),
// эти настройки заработают. До тех пор — заготовка для админа +
// читаемые значения для координации с kyc-командой.

interface Props {
  projectId: string;
  data: ProjectData;
  onChange: (patch: Partial<ProjectData>) => void;
}

const LEVEL_PRESETS = [
  { value: 'basic-kyc-level', label: 'Базовый (паспорт + селфи)' },
  { value: 'enhanced-kyc-level', label: 'Расширенный (+ proof-of-address + анкета)' },
  { value: 'trientes-kyc-level', label: 'Trientes (+ согласие)' }
];

const KycSettingsTab: React.FC<Props> = ({ projectId, data, onChange }) => {
  const kyc = data.kyc || {};
  const enabled = kyc.enabled !== false; // default true
  const levelName = kyc.levelName || 'basic-kyc-level';
  const sumsubProjectId = kyc.sumsubProjectId || projectId;
  const autoApproveDelay = kyc.autoApproveDelay ?? 0;
  const ttlInSecs = kyc.ttlInSecs ?? 600;

  const patchKyc = (patch: Partial<NonNullable<ProjectData['kyc']>>) => {
    onChange({ kyc: { ...kyc, ...patch } });
  };

  const isCustomLevel = !LEVEL_PRESETS.some(p => p.value === levelName);

  return (
    <>
      <Alert variant="info" className="py-2 small mb-3">
        <strong>KYC для проекта «{projectId}».</strong> Настройки уровня и параметров
        SumSub WebSDK. kyc-svc сейчас читает только глобальный <code>SUMSUB_LEVEL</code>
        env (Phase 0); per-project применение — после расширения <code>kyc.proto</code>
        (Phase 1, см. <code>mock_ss/docs/INTEGRATION_FRONTEND.md</code>). До тех пор
        значения сохраняются здесь как заготовка.
      </Alert>

      <Form.Check
        type="switch"
        id="kyc-enabled"
        className="mb-3"
        label="KYC включён для этого проекта"
        checked={enabled}
        onChange={e => patchKyc({ enabled: e.target.checked })}
      />

      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="small">Уровень верификации (levelName)</Form.Label>
            <Form.Select
              size="sm"
              value={isCustomLevel ? '__custom__' : levelName}
              onChange={e => {
                if (e.target.value === '__custom__') {
                  patchKyc({ levelName: '' });
                } else {
                  patchKyc({ levelName: e.target.value });
                }
              }}
              disabled={!enabled}
            >
              {LEVEL_PRESETS.map(p => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
              <option value="__custom__">Свой (custom level)</option>
            </Form.Select>
            {isCustomLevel && (
              <Form.Control
                size="sm"
                className="mt-2"
                placeholder="custom-level-name"
                value={levelName}
                onChange={e => patchKyc({ levelName: e.target.value })}
                disabled={!enabled}
              />
            )}
            <Form.Text className="text-muted">
              Соответствует <code>levelName</code> в SumSub / mock_ss. Авто-бутстрапится
              при первой ссылке (mock_ss).
            </Form.Text>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label className="small">SumSub project_id (tenant key для Phase 1)</Form.Label>
            <InputGroup size="sm">
              <Form.Control
                readOnly
                value={sumsubProjectId}
              />
            </InputGroup>
            <Form.Text className="text-muted">
              По умолчанию = id проекта в админке. В Phase 1 mock_ss станет ключом
              tenant'а (BUG-8). Обычно менять не нужно.
            </Form.Text>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label className="small">TTL access-токена (секунды)</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={60}
              max={600}
              value={ttlInSecs}
              onChange={e => {
                const v = Math.max(60, Math.min(600, parseInt(e.target.value, 10) || 600));
                patchKyc({ ttlInSecs: v });
              }}
              disabled={!enabled}
            />
            <Form.Text className="text-muted">
              60..600s. Рекомендуется ≤10 минут (security best practice).
            </Form.Text>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label className="small">
              Auto-approve delay (только mock, для CI)
            </Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              max={3600}
              value={autoApproveDelay}
              onChange={e => {
                const v = Math.max(0, Math.min(3600, parseInt(e.target.value, 10) || 0));
                patchKyc({ autoApproveDelay: v });
              }}
              disabled={!enabled}
            />
            <Form.Text className="text-muted">
              0 = выкл (ручной review). N&gt;0 — mock авто-одобряет через N секунд после
              submit. На реальном SumSub игнорируется.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Alert variant="warning" className="py-2 small mt-3 mb-0">
        <strong>Что должен сделать фронт-команда:</strong> прочитать
        <code> mock_ss/docs/INTEGRATION_FRONTEND.md</code> — там полный чек-лист
        (MVP + Phase 1). Передавать <code>X-Project-ID</code> в запросах к
        api-gateway; backend сам подставит levelName для проекта.
      </Alert>
    </>
  );
};

export default KycSettingsTab;
